import { FormEvent, useState, useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
// Models
import { IPage } from '@root/models/Page';
// State
import { useSelector } from 'react-redux';
// Selectors
import { selectColumnValue, selectWorkspaceType } from 'app/store/ui/uiSelectors';
import { selectTotalPagesInWorkspace, selectPagesInWorkspaceByPreEvent } from 'app/store/page/page.selectors';
import { selectFilteredDocumentPages } from 'app/store/page/page.selectors';
// MaterialUI
import { Box, Button, TextField, FormHelperText } from '@mui/material';
// i18next
import { useTranslation } from 'react-i18next';
import { usePagesToolbarContext } from '../utils/usePagesToolbarContext';
import { CARD_WIDTH } from 'app/App.constants';

const GoToPage = () => {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const { documentId } = useParams<{ documentId:string }>();

  const { listRef } = usePagesToolbarContext();

  const isDocumentsPath = pathname.includes('documents');

  // State
  const columnValue:string = useSelector(selectColumnValue);
  const workspaceType = useSelector(selectWorkspaceType);
  const pagesWithPreEvent:Array<IPage> | null = useSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: true }));
  const pagesWithoutPreEvent:Array<IPage> | null = useSelector((state:any) => selectPagesInWorkspaceByPreEvent(state, { preEvent: false }));
  const documentPages = useSelector((state:any) => selectFilteredDocumentPages(state, { documentId: Number(documentId) })) || [];
  const totalPages:any = useSelector(selectTotalPagesInWorkspace);

  const pages = useMemo(() => {
    // ToDO: remove this condition when we have a better solution for the files
    if ( isDocumentsPath ) return documentPages;

    const result:any = [];
    if ( pagesWithPreEvent && pagesWithPreEvent.length ){
      result.push({ type: 'placeholder', label: 'Pre-event' });
      for ( let i = 0; i < pagesWithPreEvent.length; i++ ){
        result.push({...pagesWithPreEvent[i], type: 'page', index: i });
      }
    }
    if ( pagesWithoutPreEvent && pagesWithoutPreEvent.length ){
      const index = pagesWithPreEvent ? pagesWithPreEvent.length : 0;
      result.push({ type: 'placeholder', label: 'Post-event' });
      for ( let i = 0; i < pagesWithoutPreEvent.length; i++ ){
        result.push({...pagesWithoutPreEvent[i], type: 'page', index: index + i });
      }
    }
    return result;
    // eslint-disable-next-line
  }, [pagesWithPreEvent, pagesWithoutPreEvent]);

  const [ goTo, setGoTo ] = useState<string>('');

  // Reset 'goTo' on change document
  useEffect(() => {
    setGoTo('');
    // eslint-disable-next-line
  }, [documentId])

  const handleChange = (event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setGoTo(value);
  }
  const handleSubmit = (event:FormEvent) => {
    event.preventDefault();

    if ( !listRef.current || !listRef.current.props ) return;

    const { width } = listRef.current.props;

    const pageNum = Number(goTo);
    const foundedPageIndex = pages.findIndex((_:any, index:number) => index === (pageNum - 1));

    if ( typeof foundedPageIndex === 'undefined' ) return;

    const itemsPerRow = columnValue === 'auto' ? Math.floor(width / CARD_WIDTH) : Number(columnValue);
    const rowIndex = Math.floor(foundedPageIndex/itemsPerRow);

    listRef.current.scrollToItem(rowIndex, 'center');
  }

  const total = isDocumentsPath ? documentPages?.length : (totalPages?.totalMainPagesInWorkspace || 0);

  if ( workspaceType !== 'pages' ) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center'
      }}
      onSubmit={handleSubmit}
      component="form"
    >
      <TextField
        aria-label="Go to input"
        sx={{
          width: 128
        }}
        value={goTo}
        onChange={handleChange}
        margin="none"
        placeholder="1"
        slotProps={{
          input: {
            sx: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              pr: 0,
              height: 40,
              py: '6px'
            },
            endAdornment: (
              <FormHelperText
                sx={{ p: 0, mt: 0, fontSize: 16, whiteSpace: 'nowrap' }}
              >{`/ ${total}`}</FormHelperText>
            )
          }
        }}
      />
      <Button
        sx={{
          whiteSpace: 'nowrap',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          height: 40
        }}
        type="submit"
        disabled={goTo && (Number(goTo) > 0 && Number(goTo) <= total) ? false : true}
        size="small"
        variant="contained"
      >{t('buttons.goToPage')}</Button>
    </Box>
  )
}

export default GoToPage;
